/**
 * Phase 2b redirect map for the back office. ACTIVE: connected in
 * src/routes/admin.tsx (beforeLoad of the /admin layout), so it runs for every
 * /admin/* URL before the page loads. Retired routes keep a minimal redirect-only file forever.
 * /admin/diagnostico?historico=1 stays reachable as the archive of the old micro diagnosis.
 * Existing query params are preserved; the target's own params (tab/origen) win.
 */
export const ADMIN_REDIRECTS_ENABLED = true as const;

type Rule = { from: string; whenTab?: string; unlessParam?: string; to: string; params: Record<string, string> };

export const ADMIN_REDIRECT_RULES: readonly Rule[] = [
  { from: "/admin/embudo", to: "/admin/analitica", params: { tab: "herramientas" } },
  { from: "/admin/diagnostico", unlessParam: "historico", to: "/admin/analitica", params: { tab: "herramientas" } },
  { from: "/admin/calculator", to: "/admin/analitica", params: { tab: "herramientas" } },
  { from: "/admin/landings", to: "/admin/analitica", params: { tab: "landings" } },
  { from: "/admin/waitlist", to: "/admin/leads", params: { origen: "waitlist" } },
  { from: "/admin/diagnosticador-locales", whenTab: "leads", to: "/admin/leads", params: { origen: "diagnosticador" } },
  { from: "/admin/comparativa-planes", to: "/admin/planes", params: { tab: "comparativa" } },
  // Fase 3: rutas ocultas retiradas.
  { from: "/admin/facturacion", to: "/admin/finanzas", params: { tab: "invoices" } },
  { from: "/admin/facturas", to: "/admin/finanzas", params: { tab: "invoices" } },
  { from: "/admin/payments", to: "/admin/finanzas", params: { tab: "transactions" } },
  { from: "/admin/tips", to: "/admin/mindset-quotes", params: {} },
  { from: "/admin/pricing-settings", to: "/admin/pricing", params: {} },
];

/** Back-compat view: old path -> new path (without params). */
export const ADMIN_REDIRECTS: Readonly<Record<string, string>> = Object.fromEntries(
  ADMIN_REDIRECT_RULES.filter((r) => !r.whenTab).map((r) => [r.from, r.to]),
);

/** Returns the full target href (path + query) or null when the URL does not move. */
export function resolveAdminRedirect(pathname: string, search: string = ""): string | null {
  if (!ADMIN_REDIRECTS_ENABLED) return null;
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const rule = ADMIN_REDIRECT_RULES.find((r) => r.from === path && (!r.whenTab || params.get("tab") === r.whenTab) && !(r.unlessParam && params.has(r.unlessParam)));
  if (!rule) return null;
  if (rule.whenTab) params.delete("tab");
  for (const [k, v] of Object.entries(rule.params)) params.set(k, v);
  const qs = params.toString();
  return qs ? `${rule.to}?${qs}` : rule.to;
}
